/* ============================================================================
   ANANDA Pricing — interactive circular pricing clock
   Companion to sections/ananda-pricing.liquid + assets/ananda-pricing.css

   The user picks a quantity (4..24) by:
     • dragging the glowing knob around the arc,
     • clicking anywhere on the ring (or a number) to animate there, or
     • focusing the dial and using the keyboard (arrows / Home / End / PageUp/Dn).

   The centre live-updates per-unit price, discount %, and total. The total is
   rounded UP to a whole shekel; per-unit is the discounted unit price
   (base × (1 − discount)), constant across a tier.

   Motion (Roi):
     • COUNT-UP — when the quantity is changed by a click / keypress / entrance,
       the per-unit and total figures count smoothly to their new value (during
       a live drag they track the finger instantly instead).
     • ENTRANCE — when the section scrolls into view it fades/scales/rotates in,
       then the knob sweeps from the minimum up to the default quantity while the
       prices count up (a circular reveal).

   Geometry MUST match the baked SVG in the Liquid: viewBox 0..100, centre 50,
   radius 36, a 60° gap at the top, quantity 4 at +30° (clockwise from top)
   through 24 at +330°.
   ========================================================================== */
(function () {
  'use strict';

  var MIN = 4;
  var MAX = 24;
  var C = 50;          // viewBox centre
  var R = 36;          // track radius
  var GAP = 60;        // top gap (degrees)
  var START = GAP / 2; // angle of MIN (degrees, clockwise from top)
  var SWEEP = 360 - GAP;

  function clampInt(n) {
    n = Math.round(n);
    return n < MIN ? MIN : (n > MAX ? MAX : n);
  }
  function angOf(p) { return START + (p - MIN) / (MAX - MIN) * SWEEP; }
  function ptOf(deg, rad) {
    var a = deg * Math.PI / 180;
    return [C + rad * Math.sin(a), C - rad * Math.cos(a)];
  }
  // Arc path between two quantities (each segment is ≤ 75°, so large-arc-flag = 0).
  function arcBetween(a, b) {
    if (b <= a + 0.02) return '';
    var s = ptOf(angOf(a), R), e = ptOf(angOf(b), R);
    return 'M ' + s[0].toFixed(2) + ' ' + s[1].toFixed(2) +
           ' A ' + R + ' ' + R + ' 0 0 1 ' + e[0].toFixed(2) + ' ' + e[1].toFixed(2);
  }
  /* Four brand-colour zones across the MIN..MAX range — ALOHA / GAIA / KYOTO /
     SANTORINI, one solid colour per quantity band, revealed as the arc fills. */
  var SEG_BOUNDS = [4, 9, 14, 19, 24];

  /* Pricing model — Roi's FIXED tier table, derived from the base unit price.
     Discount only "unlocks" at multiples of 4; in-between quantities keep the
     discount of the last reached tier: 4-7→0%, 8-11→8%, 12-15→12%, 16-19→16%,
     20-23→20%, 24+→24%. (Each major anchor equals its own discount %.) */
  function discountPct(q) {
    var anchor = Math.min(MAX, Math.floor(q / 4) * 4);
    return anchor <= MIN ? 0 : anchor;
  }
  function priceFor(q, base) {
    var d = discountPct(q) / 100;
    var total = Math.ceil(q * base * (1 - d));   // agorot rounded UP to a whole ₪
    var unit = base * (1 - d);                   // per-unit after discount (constant per tier)
    return { pct: discountPct(q), total: total, unit: unit };
  }
  function fmtMoney(x) {
    var r = Math.round(x * 100) / 100;
    if (Math.abs(r - Math.round(r)) < 0.005) return String(Math.round(r));
    return r.toFixed(2).replace(/0$/, '').replace(/\.$/, '');
  }
  var easeOut = function (t) { return 1 - Math.pow(1 - t, 3); };

  /* Split `qty` across `n` flavours: a floor(qty/n) base for each, then the
     remainder (qty mod n) handed to that many RANDOM DISTINCT flavours, +1 each
     (so e.g. 15 over 4 = three flavours of 4 + one of 3 — one stays out of the
     bonus). With the dial min of 4 every flavour always gets at least 1. */
  function distributeMix(qty, n) {
    var base = Math.floor(qty / n), rem = qty % n;
    var counts = [], idx = [];
    for (var i = 0; i < n; i++) { counts.push(base); idx.push(i); }
    for (var k = idx.length - 1; k > 0; k--) {        // Fisher–Yates shuffle
      var j = Math.floor(Math.random() * (k + 1));
      var t = idx[k]; idx[k] = idx[j]; idx[j] = t;
    }
    for (var r = 0; r < rem; r++) counts[idx[r]] += 1;
    return counts;
  }

  /* Press feedback on a REAL click — restarts a one-shot press animation (CSS
     .is-clicked) so even rapid taps each "pop"; a timer clears it after the press. */
  function playPress(btn) {
    btn.classList.remove('is-clicked');
    void btn.offsetWidth;                  // reflow → restart the animation on rapid clicks
    btn.classList.add('is-clicked');
    clearTimeout(btn._pressT);
    btn._pressT = setTimeout(function () { btn.classList.remove('is-clicked'); }, 300);
  }

  /* "Add a mix" — enqueue one click's worth of the 4-flavour mix. Clicks are
     SERIALIZED (a queue) so rapid taps accumulate correctly: each run reads the
     LIVE cart and CLEARS it ONLY when it holds fewer than 24 units; at 24+ it
     ADDS on top instead. So repeated max-dial (24) clicks quickly fill the cart in
     multiples of 24. NOTE (future): this clear/accumulate rule is the hook where
     stock-aware allocation will live. */
  function addMix(button, variantIds, getQty) {
    if (!variantIds.length) return;
    button._mixQ = button._mixQ || [];
    button._mixQ.push(getQty());             // capture the quantity at click time
    if (button.dataset.busy === '1') return; // a run is active — it will drain the queue
    runMix(button, variantIds);
  }

  function runMix(button, variantIds) {
    if (!button._mixQ || !button._mixQ.length) return;
    var qty = button._mixQ.shift();
    button.dataset.busy = '1';
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('data-status', 'עריכת עגלה');

    var counts = distributeMix(qty, variantIds.length);
    var items = [];
    for (var i = 0; i < variantIds.length; i++) {
      if (counts[i] > 0) items.push({ id: variantIds[i], quantity: counts[i] });
    }

    var drawer = document.querySelector('cart-drawer');
    var sectionIds = (drawer && typeof drawer.getSectionsToRender === 'function')
      ? drawer.getSectionsToRender().map(function (s) { return s.id; }) : null;

    function finish(ok) {
      button.dataset.busy = '';
      button.removeAttribute('aria-busy');
      if (button._mixQ && button._mixQ.length) { runMix(button, variantIds); return; }  // drain the queue
      button.setAttribute('data-status', ok ? '✓ נוסף לעגלה' : 'אופס — נסו שוב');
      setTimeout(function () { button.removeAttribute('data-status'); }, 2000);
    }

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // clear ONLY if the cart holds fewer than 24 units; at 24+ accumulate on top.
        if (cart && cart.item_count < 24) {
          return fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } });
        }
      })
      .then(function () {
        var body = { items: items };
        if (sectionIds) { body.sections = sectionIds; body.sections_url = window.location.pathname; }
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
      })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function (res) {
        var rendered = false;
        try {
          if (drawer && typeof drawer.renderContents === 'function' && res && res.sections) {
            if (drawer.classList) drawer.classList.remove('is-empty');
            drawer.renderContents(res);     // refresh drawer + bubble AND open it
            rendered = true;
          }
        } catch (e) { rendered = false; }
        // no drawer API + nothing left queued → fall back to the cart page
        if (!rendered && !(button._mixQ && button._mixQ.length)) { window.location.href = '/cart'; return; }
        finish(true);
      })
      .catch(function () { finish(false); });
  }

  function initOne(root) {
    if (!root || root.dataset.aprInit === '1') return;
    root.dataset.aprInit = '1';

    var base = parseFloat(root.getAttribute('data-apr-base')) || 28;
    var qDefault = clampInt(parseInt(root.getAttribute('data-apr-default'), 10) || 8);

    var svg = root.querySelector('[data-apr-svg]');
    var dial = root.querySelector('[data-apr-dial]');
    var segs = root.querySelectorAll('[data-apr-seg]');
    var knobs = root.querySelectorAll('[data-apr-knob]');
    var labels = root.querySelectorAll('.apr-num');
    var elUnit = root.querySelector('[data-apr-unit]');
    var elTotal = root.querySelector('[data-apr-total]');
    var elTotalOrig = root.querySelector('[data-apr-total-orig]');   // struck pre-discount total
    var origWraps = root.querySelectorAll('[data-apr-orig]');        // both struck-original wrappers
    var elDisc = root.querySelector('[data-apr-disc]');
    var elDiscWrap = root.querySelector('[data-apr-disc-wrap]');
    var qtyEls = root.querySelectorAll('[data-apr-qty]');
    var mixAddWord = root.querySelector('[data-apr-mix-add]');   // "הוסיפו " — shown only at the max (24+)
    var unitMoney = root.querySelector('.ananda-pricing__unit .ananda-pricing__money');
    var totalMoney = root.querySelector('.ananda-pricing__total .ananda-pricing__money');
    if (!svg || !dial || !segs.length) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var q = qDefault;          // settled quantity
    var currentP = qDefault;   // continuous knob progress
    var lastN = -1;            // last integer painted (dial visuals)
    var raf = null;            // knob/arc tween handle
    var priceRaf = null;       // price count handle
    var bumpTimer = null;
    var dragging = false, moved = false, downP = MIN, startX = 0, startY = 0;
    var entered = false;

    var dispUnit = priceFor(qDefault, base).unit;   // currently shown figures
    var dispTotal = priceFor(qDefault, base).total;
    var dispTotalOrig = Math.ceil(qDefault * base); // struck pre-discount total (counts too)

    function bump() {
      if (reduce || !unitMoney) return;
      unitMoney.classList.add('is-bumped');
      if (totalMoney) totalMoney.classList.add('is-bumped');
      clearTimeout(bumpTimer);
      bumpTimer = setTimeout(function () {
        unitMoney.classList.remove('is-bumped');
        if (totalMoney) totalMoney.classList.remove('is-bumped');
      }, 220);
    }

    var cartCount = 0;          // live total units in the cart (kept in sync below)
    /* "הוסיפו" shows at the MAX dial (n===MAX) OR whenever the cart already holds
       24+ units — because at 24+ a click ADDS on top (accumulate), so "add" is true. */
    function setAddWord(n) {
      if (mixAddWord) mixAddWord.hidden = !(n === MAX || cartCount >= 24);
    }
    var cartFetchT;
    function refreshCartCount() {
      clearTimeout(cartFetchT);
      cartFetchT = setTimeout(function () {
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (c) { cartCount = (c && c.item_count) || 0; setAddWord(lastN); })
          .catch(function () {});
      }, 120);
    }

    /* Dial visuals: knob, active arc, quantity pill, discount, active number. */
    function paint(p) {
      if (p < MIN) p = MIN; if (p > MAX) p = MAX;
      var k = ptOf(angOf(p), R);
      for (var i = 0; i < knobs.length; i++) {
        knobs[i].setAttribute('cx', k[0].toFixed(2));
        knobs[i].setAttribute('cy', k[1].toFixed(2));
      }
      for (var si = 0; si < segs.length; si++) {
        segs[si].setAttribute('d', arcBetween(SEG_BOUNDS[si], Math.min(p, SEG_BOUNDS[si + 1])));
      }

      var n = Math.round(p);
      if (n !== lastN) {
        var pct = discountPct(n);
        if (elDisc) elDisc.textContent = String(pct);
        if (elDiscWrap) { if (pct > 0) elDiscWrap.removeAttribute('hidden'); else elDiscWrap.setAttribute('hidden', ''); }
        for (var oi = 0; oi < origWraps.length; oi++) {   // struck originals only when there's a discount
          if (pct > 0) origWraps[oi].removeAttribute('hidden'); else origWraps[oi].setAttribute('hidden', '');
        }
        for (var j = 0; j < qtyEls.length; j++) qtyEls[j].textContent = String(n);
        setAddWord(n);   // "הוסיפו" — at the max dial, or when the cart already holds 24+
        for (var m = 0; m < labels.length; m++) {
          labels[m].classList.toggle('is-active', parseInt(labels[m].getAttribute('data-q'), 10) === n);
        }
        dial.setAttribute('aria-valuenow', String(n));
        dial.setAttribute('aria-valuetext', n + ' יחידות, ' + priceFor(n, base).total + ' שקלים');
        lastN = n;
      }
    }

    /* Price figures — count from the currently-shown values to qty n's values. */
    function renderPrice() {
      if (elUnit) elUnit.textContent = fmtMoney(dispUnit);
      if (elTotal) elTotal.textContent = String(Math.round(dispTotal));
      if (elTotalOrig) elTotalOrig.textContent = String(Math.round(dispTotalOrig));
    }
    function setPrices(n, animate) {
      var tU = priceFor(n, base).unit, tT = priceFor(n, base).total, tTo = Math.ceil(n * base);
      cancelAnimationFrame(priceRaf);
      if (!animate || reduce) { dispUnit = tU; dispTotal = tT; dispTotalOrig = tTo; renderPrice(); return; }
      if (dispUnit === tU && dispTotal === tT) { dispTotalOrig = tTo; renderPrice(); return; }
      var fU = dispUnit, fT = dispTotal, fTo = dispTotalOrig, t0 = null, dur = 520;
      function step(now) {
        if (t0 === null) t0 = now;
        var t = Math.min(1, (now - t0) / dur), e = easeOut(t);
        dispUnit = fU + (tU - fU) * e;
        dispTotal = fT + (tT - fT) * e;
        dispTotalOrig = fTo + (tTo - fTo) * e;
        renderPrice();
        if (t < 1) priceRaf = requestAnimationFrame(step);
        else { dispUnit = tU; dispTotal = tT; dispTotalOrig = tTo; renderPrice(); bump(); }
      }
      priceRaf = requestAnimationFrame(step);
    }

    /* Animate the knob/arc to a settled integer AND count the prices to it. */
    function tweenTo(target) {
      target = clampInt(target);
      q = target;
      setPrices(target, true);
      cancelAnimationFrame(raf);
      if (reduce) { currentP = target; paint(target); return; }
      var from = currentP, to = target, t0 = null, dur = 360;
      function step(now) {
        if (t0 === null) t0 = now;
        var t = Math.min(1, (now - t0) / dur);
        currentP = from + (to - from) * easeOut(t);
        paint(currentP);
        if (t < 1) raf = requestAnimationFrame(step);
        else { currentP = to; paint(to); }
      }
      raf = requestAnimationFrame(step);
    }

    /* Pointer angle → progress. `guard` stops a drag from leaping across the
       top gap (MAX↔MIN) when the finger passes over the empty top. */
    function progressFromEvent(e, guard) {
      var r = svg.getBoundingClientRect();
      var dx = e.clientX - (r.left + r.width / 2);
      var dy = e.clientY - (r.top + r.height / 2);
      var deg = Math.atan2(dx, -dy) * 180 / Math.PI;
      if (deg < 0) deg += 360;
      if (deg < START) deg = START;
      else if (deg > 360 - START) deg = 360 - START;
      var p = MIN + (deg - START) / SWEEP * (MAX - MIN);
      if (p < MIN) p = MIN; if (p > MAX) p = MAX;
      if (guard && Math.abs(p - currentP) > (MAX - MIN) * 0.6) {
        p = (currentP > (MIN + MAX) / 2) ? MAX : MIN;
      }
      return p;
    }

    function onDown(e) {
      // Ignore presses near the centre (read-out) so tapping the price doesn't snap.
      var r = svg.getBoundingClientRect();
      var ddx = e.clientX - (r.left + r.width / 2);
      var ddy = e.clientY - (r.top + r.height / 2);
      if (Math.sqrt(ddx * ddx + ddy * ddy) < r.width * 0.27) return;
      dragging = true; moved = false;
      startX = e.clientX; startY = e.clientY;
      downP = progressFromEvent(e, false);
      try { dial.focus({ preventScroll: true }); } catch (_) { dial.focus(); }
      try { svg.setPointerCapture(e.pointerId); } catch (_) {}
      e.preventDefault();
    }
    function onMove(e) {
      if (!dragging) return;
      if (!moved) {
        var dx = e.clientX - startX, dy = e.clientY - startY;
        if (dx * dx + dy * dy < 16) return;     // < 4px: still a click
        moved = true;
        root.classList.add('is-grabbing');
        cancelAnimationFrame(raf);
      }
      currentP = progressFromEvent(e, true);
      paint(currentP);
      setPrices(Math.round(currentP), false);   // track the finger instantly
      q = Math.round(currentP);
      e.preventDefault();
    }
    function onUp(e) {
      if (!dragging) return;
      dragging = false;
      root.classList.remove('is-grabbing');
      try { svg.releasePointerCapture(e.pointerId); } catch (_) {}
      if (moved) tweenTo(Math.round(currentP));  // settle the drag (prices already there)
      else tweenTo(Math.round(downP));           // a click → animate + count up/down
    }

    svg.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    dial.addEventListener('keydown', function (e) {
      var handled = true;
      switch (e.key) {
        case 'ArrowUp': case 'ArrowRight': tweenTo(q + 1); break;
        case 'ArrowDown': case 'ArrowLeft': tweenTo(q - 1); break;
        case 'PageUp': tweenTo(q + 4); break;
        case 'PageDown': tweenTo(q - 4); break;
        case 'Home': tweenTo(MIN); break;
        case 'End': tweenTo(MAX); break;
        default: handled = false;
      }
      if (handled) e.preventDefault();
    });

    /* Entrance — fade/scale in (CSS), then sweep MIN → default while counting. */
    function enter() {
      if (entered) return;
      entered = true;
      if (reduce) {
        root.classList.add('is-entered');
        currentP = qDefault; q = qDefault; lastN = -1; paint(qDefault); setPrices(qDefault, false);
        return;
      }
      currentP = MIN; q = MIN; lastN = -1; paint(MIN);
      dispUnit = priceFor(MIN, base).unit; dispTotal = priceFor(MIN, base).total; renderPrice();
      root.classList.add('is-entered');
      setTimeout(function () { tweenTo(qDefault); }, 340);
    }

    root.classList.add('is-ready');

    /* "Add a mix" button — adds the 4 flavours for the CURRENT quantity (q). */
    var mixBtn = root.querySelector('[data-apr-mix]');
    if (mixBtn) {
      var mixVariants = (mixBtn.getAttribute('data-apr-mix-variants') || '')
        .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      mixBtn.addEventListener('click', function () {
        playPress(mixBtn);
        addMix(mixBtn, mixVariants, function () { return q; });
      });
      // Keep "הוסיפו" in sync with the live cart total (24+ → accumulate mode):
      // fetch once now, then re-fetch whenever the header cart bubble re-renders
      // (which Dawn does on every cart change — add, remove, quantity edit).
      refreshCartCount();
      var cartBubble = document.getElementById('cart-icon-bubble');
      if (cartBubble && 'MutationObserver' in window) {
        new MutationObserver(function () { refreshCartCount(); }).observe(cartBubble, { childList: true, subtree: true });
      }
    }

    if (reduce || !('IntersectionObserver' in window)) {
      enter();
    } else {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { io.disconnect(); enter(); break; }
        }
      }, { threshold: 0.2 });
      io.observe(root);
      // Safety net: if the observer never fires (e.g. odd editor states), reveal anyway.
      setTimeout(function () { if (!entered) { io.disconnect(); enter(); } }, 1800);
    }
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-apr]');
    for (var i = 0; i < nodes.length; i++) initOne(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
